import React, { useState, useEffect } from 'react';
import './Zmiany.css';
import shiftsAPI from '../../api/shifts';

function Zmiany({ projects = [] }) {
  const [selectedProject, setSelectedProject] = useState(projects.length > 0 ? projects[0] : '');
  const [shifts, setShifts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    startTime: '06:00',
    endTime: '14:00',
    color: '#667eea'
  });

  // Kolory dla zmian
  const defaultColors = ['#ef4444', '#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'];
  const [customColors, setCustomColors] = useState([]);
  const [showColorModal, setShowColorModal] = useState(false);
  const [newColorValue, setNewColorValue] = useState('#667eea');
  
  // Stany dla modali
  const [showDeleteShiftModal, setShowDeleteShiftModal] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');

  // Wczytaj zapisane kolory z localStorage
  useEffect(() => {
    const savedColors = localStorage.getItem('customShiftColors');
    if (savedColors) {
      setCustomColors(JSON.parse(savedColors));
    }
  }, []);

  // Wczytaj zmiany dla wybranego projektu z API
  useEffect(() => {
    const loadShifts = async () => {
      if (!selectedProject) {
        setShifts([]);
        return;
      }
      
      try {
        const shiftsData = await shiftsAPI.getByProject(selectedProject);
        // Konwertuj format - jeśli to stringi, zamień na obiekty
        const formattedShifts = shiftsData.map((shift, index) => {
          if (typeof shift === 'string') {
            // Stary format - tylko nazwa
            return {
              id: Date.now() + index,
              name: shift,
              startTime: '06:00',
              endTime: '14:00',
              duration: 8,
              color: defaultColors[index % defaultColors.length]
            };
          } else {
            // Nowy format - pełny obiekt
            return shift;
          }
        });
        setShifts(formattedShifts);
      } catch (error) {
        console.error('Błąd ładowania zmian:', error);
        setShifts([]);
      }
    };
    
    loadShifts();
    
    // Nasłuchuj aktualizacji zmian
    const handleShiftsUpdate = () => {
      loadShifts();
    };
    window.addEventListener('shifts-updated', handleShiftsUpdate);
    
    return () => {
      window.removeEventListener('shifts-updated', handleShiftsUpdate);
    };
  }, [selectedProject]);

  // Zapisz kolory do localStorage
  const saveCustomColors = (colors) => {
    localStorage.setItem('customShiftColors', JSON.stringify(colors));
    setCustomColors(colors);
  };

  // Otwórz modal do dodawania koloru
  const handleOpenColorModal = () => {
    setNewColorValue('#667eea');
    setShowColorModal(true);
  };

  const showInfo = (message) => {
    setInfoMessage(message);
    setShowInfoModal(true);
  };

  // Dodaj kolor do zapisanych z modala
  const handleAddColorFromModal = () => {
    if (!newColorValue) return;
    
    // Sprawdź czy kolor już istnieje
    if (defaultColors.includes(newColorValue) || customColors.includes(newColorValue)) {
      showInfo('Ten kolor już istnieje w palecie');
      return;
    }
    
    const newColors = [...customColors, newColorValue];
    saveCustomColors(newColors);
    setFormData({ ...formData, color: newColorValue });
    setShowColorModal(false);
  };

  // Anuluj dodawanie koloru
  const handleCancelColorModal = () => {
    setShowColorModal(false);
    setNewColorValue('#667eea');
  };

  // Usuń zapisany kolor
  const handleRemoveColor = (colorToRemove, e) => {
    e.stopPropagation();
    const newColors = customColors.filter(c => c !== colorToRemove);
    saveCustomColors(newColors);
    
    // Jeśli usuwany kolor był wybrany, wybierz pierwszy dostępny
    if (formData.color === colorToRemove) {
      const availableColors = [...defaultColors, ...newColors];
      if (availableColors.length > 0) {
        setFormData({ ...formData, color: availableColors[0] });
      }
    }
  };

  // Wszystkie dostępne kolory
  const allColors = [...defaultColors, ...customColors];


  const getShiftInitials = (name) => {
    return name.substring(0, 2).toUpperCase();
  };

  const calculateDuration = (start, end) => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    let startTotal = startHour * 60 + startMin;
    let endTotal = endHour * 60 + endMin;
    
    // Obsługa zmian nocnych (przechodzących przez północ)
    if (endTotal < startTotal) {
      endTotal += 24 * 60;
    }
    
    const duration = endTotal - startTotal;
    return duration / 60; // w godzinach
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const handleAddShift = async () => {
    if (!formData.name.trim()) {
      showInfo('Proszę podać nazwę zmiany');
      return;
    }

    const duration = calculateDuration(formData.startTime, formData.endTime);
    
    const newShift = {
      id: Date.now(),
      name: formData.name,
      startTime: formData.startTime,
      endTime: formData.endTime,
      duration: duration,
      color: formData.color
    };

    try {
      // Zapisz przez API
      await shiftsAPI.add(selectedProject, newShift);
      
      // Odśwież listę zmian
      const shiftsData = await shiftsAPI.getByProject(selectedProject);
      const formattedShifts = shiftsData.map((shift, index) => {
        if (typeof shift === 'string') {
          return {
            id: Date.now() + index,
            name: shift,
            startTime: '06:00',
            endTime: '14:00',
            duration: 8,
            color: defaultColors[index % defaultColors.length]
          };
        } else {
          return shift;
        }
      });
      setShifts(formattedShifts);
      
      // Wyślij event o aktualizacji
      window.dispatchEvent(new CustomEvent('shifts-updated'));
      
      // Reset formularza
      setFormData({
        name: '',
        startTime: '06:00',
        endTime: '14:00',
        color: defaultColors[formattedShifts.length % defaultColors.length]
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Błąd dodawania zmiany:', error);
      showInfo('Nie udało się dodać zmiany. Spróbuj ponownie.');
    }
  };

  const handleEditShift = (shift) => {
    setEditingShift(shift);
    setFormData({
      name: shift.name,
      startTime: shift.startTime,
      endTime: shift.endTime,
      color: shift.color
    });
    setShowAddForm(true);
  };

  const handleUpdateShift = async () => {
    if (!formData.name.trim()) {
      showInfo('Proszę podać nazwę zmiany');
      return;
    }

    const duration = calculateDuration(formData.startTime, formData.endTime);
    
    const updatedShift = {
      id: editingShift.id,
      name: formData.name,
      startTime: formData.startTime,
      endTime: formData.endTime,
      duration: duration,
      color: formData.color
    };

    try {
      // Usuń starą zmianę i dodaj zaktualizowaną
      await shiftsAPI.remove(selectedProject, editingShift.name);
      await shiftsAPI.add(selectedProject, updatedShift);
      
      // Odśwież listę zmian
      const shiftsData = await shiftsAPI.getByProject(selectedProject);
      const formattedShifts = shiftsData.map((shift, index) => {
        if (typeof shift === 'string') {
          return {
            id: Date.now() + index,
            name: shift,
            startTime: '06:00',
            endTime: '14:00',
            duration: 8,
            color: defaultColors[index % defaultColors.length]
          };
        } else {
          return shift;
        }
      });
      setShifts(formattedShifts);
      
      // Wyślij event o aktualizacji
      window.dispatchEvent(new CustomEvent('shifts-updated'));
      
      setShowAddForm(false);
      setEditingShift(null);
      setFormData({
        name: '',
        startTime: '06:00',
        endTime: '14:00',
        color: '#667eea'
      });
    } catch (error) {
      console.error('Błąd aktualizacji zmiany:', error);
      showInfo('Nie udało się zaktualizować zmiany. Spróbuj ponownie.');
    }
  };

  const handleDeleteShift = (id) => {
    setShiftToDelete(id);
    setShowDeleteShiftModal(true);
  };

  const handleDeleteShiftConfirm = async () => {
    if (shiftToDelete) {
      try {
        const shiftToRemove = shifts.find(s => s.id === shiftToDelete);
        if (shiftToRemove) {
          await shiftsAPI.remove(selectedProject, shiftToRemove.name);
          
          // Odśwież listę zmian
          const shiftsData = await shiftsAPI.getByProject(selectedProject);
          const formattedShifts = shiftsData.map((shift, index) => {
            if (typeof shift === 'string') {
              return {
                id: Date.now() + index,
                name: shift,
                startTime: '06:00',
                endTime: '14:00',
                duration: 8,
                color: defaultColors[index % defaultColors.length]
              };
            } else {
              return shift;
            }
          });
          setShifts(formattedShifts);
          
          // Wyślij event o aktualizacji
          window.dispatchEvent(new CustomEvent('shifts-updated'));
        }
        
        setShowDeleteShiftModal(false);
        setShiftToDelete(null);
      } catch (error) {
        console.error('Błąd usuwania zmiany:', error);
        showInfo('Nie udało się usunąć zmiany. Spróbuj ponownie.');
      }
    }
  };

  const handleDeleteShiftCancel = () => {
    setShowDeleteShiftModal(false);
    setShiftToDelete(null);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingShift(null);
    setFormData({
      name: '',
      startTime: '06:00',
      endTime: '14:00',
      color: '#667eea'
    });
  };

  if (projects.length === 0) {
    return (
      <div className="zmiany-container">
        <div className="empty-state">
          <p>Brak dostępnych klientów. Najpierw dodaj klienta w menu Admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="zmiany-container">
      <div className="zmiany-header">
        <h3>Zmiany</h3>
        {!showAddForm && (
          <button 
            className="add-shift-btn"
            onClick={() => setShowAddForm(true)}
            disabled={!selectedProject}
          >
            +
          </button>
        )}
      </div>

      {projects.length > 0 && (
        <div className="project-selector">
          <label htmlFor="project-select">Klient:</label>
          <select
            id="project-select"
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              setShowAddForm(false);
              setEditingShift(null);
            }}
          >
            {projects.map((project, idx) => (
              <option key={idx} value={project}>{project}</option>
            ))}
          </select>
        </div>
      )}

      {showAddForm && (
        <div className="add-shift-form">
          <h4>{editingShift ? 'Edytuj zmianę' : 'Dodaj nową zmianę'}</h4>
          <div className="form-group">
            <label>Nazwa zmiany</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="np. Rano, Południe, Noc"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Godzina rozpoczęcia</label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Godzina zakończenia</label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Kolor</label>
            <div className="color-picker">
              <div className="color-options-group">
                {defaultColors.map((color, index) => (
                  <button
                    key={`default-${index}`}
                    className={`color-option ${formData.color === color ? 'active' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                    title={color}
                  />
                ))}
                {customColors.map((color, index) => (
                  <div key={`custom-${index}`} className="custom-color-wrapper">
                    <button
                      className={`color-option ${formData.color === color ? 'active' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData({ ...formData, color })}
                      title={color}
                    />
                    <button
                      className="remove-color-btn"
                      onClick={(e) => handleRemoveColor(color, e)}
                      title="Usuń kolor"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="color-picker-actions">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="color-input"
                  title="Wybierz kolor"
                />
                <button
                  type="button"
                  className="add-color-btn"
                  onClick={handleOpenColorModal}
                  title="Dodaj nowy kolor do palety"
                >
                  + Dodaj kolor
                </button>
              </div>
            </div>
          </div>
          {formData.startTime && formData.endTime && (
            <div className="duration-preview">
              Czas trwania: {formatDuration(calculateDuration(formData.startTime, formData.endTime))}
            </div>
          )}
          <div className="form-actions">
            <button 
              className="btn-cancel"
              onClick={handleCancel}
            >
              Anuluj
            </button>
            <button 
              className="btn-save"
              onClick={editingShift ? handleUpdateShift : handleAddShift}
            >
              {editingShift ? 'Zapisz' : 'Dodaj'}
            </button>
          </div>
        </div>
      )}

      <div className="shifts-list">
        {shifts.length === 0 ? (
          <div className="empty-state">
            <p>Brak zdefiniowanych zmian. Dodaj pierwszą zmianę, aby rozpocząć.</p>
          </div>
        ) : (
          shifts.map(shift => (
            <div key={shift.id} className="shift-item">
              <div 
                className="shift-icon"
                style={{ backgroundColor: shift.color }}
              >
                {getShiftInitials(shift.name)}
              </div>
              <div className="shift-details">
                <div className="shift-name">{shift.name}</div>
                <div className="shift-time">
                  {shift.startTime} - {shift.endTime} • {formatDuration(shift.duration)}
                </div>
              </div>
              <div className="shift-actions">
                <button 
                  className="edit-btn"
                  onClick={() => handleEditShift(shift)}
                  title="Edytuj"
                >
                  ✏️
                </button>
                <button 
                  className="delete-btn"
                  onClick={() => handleDeleteShift(shift.id)}
                  title="Usuń"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal do dodawania koloru */}
      {showColorModal && (
        <div className="modal-overlay" onClick={handleCancelColorModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Dodaj nowy kolor</h3>
            <div className="modal-form-group">
              <label htmlFor="color-hex">Kod HEX:</label>
              <div className="color-input-group">
                <input
                  type="text"
                  id="color-hex"
                  value={newColorValue}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Automatycznie dodaj # jeśli brakuje
                    if (value && !value.startsWith('#')) {
                      value = '#' + value;
                    }
                    // Ogranicz do 7 znaków (# + 6 hex)
                    if (value.length <= 7) {
                      setNewColorValue(value);
                    }
                  }}
                  placeholder="#667eea"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  className="color-hex-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddColorFromModal();
                    } else if (e.key === 'Escape') {
                      handleCancelColorModal();
                    }
                  }}
                  autoFocus
                />
                <input
                  type="color"
                  value={newColorValue}
                  onChange={(e) => setNewColorValue(e.target.value)}
                  className="color-picker-input"
                  title="Wybierz kolor"
                />
              </div>
              <div className="color-preview" style={{ backgroundColor: newColorValue }}>
                <span>Podgląd koloru</span>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={handleCancelColorModal}
              >
                Anuluj
              </button>
              <button 
                className="btn-confirm"
                onClick={handleAddColorFromModal}
                disabled={!newColorValue || newColorValue.length !== 7 || defaultColors.includes(newColorValue) || customColors.includes(newColorValue)}
              >
                Dodaj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia zmiany */}
      {showDeleteShiftModal && (
        <div className="modal-overlay" onClick={handleDeleteShiftCancel}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Potwierdź usunięcie</h3>
              <button className="modal-close" onClick={handleDeleteShiftCancel}>×</button>
            </div>
            <div style={{ padding: '32px' }}>
              <p>Czy na pewno chcesz usunąć tę zmianę?</p>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={handleDeleteShiftCancel}>
                  Anuluj
                </button>
                <button className="btn-delete" onClick={handleDeleteShiftConfirm}>
                  Usuń
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal informacyjny */}
      {showInfoModal && (
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Informacja</h3>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>×</button>
            </div>
            <div style={{ padding: '32px' }}>
              <p>{infoMessage}</p>
              <div className="modal-actions">
                <button className="btn-confirm" onClick={() => setShowInfoModal(false)}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Zmiany;

